import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { meetingsApi } from '../../../services/api/meetingsApi';

/**
 * MeetingTypesSelector - A reusable component for selecting multiple meeting types
 * Fetches meeting types internally from the API
 *
 * @param {Array} selectedTypeIds - Array of selected meeting type IDs
 * @param {Function} onToggle - Callback when a type is toggled (typeId) => void
 * @param {boolean} disabled - Whether the selector is disabled
 * @param {string} helperText - Optional helper text to display
 */
const MeetingTypesSelector = ({
  selectedTypeIds = [],
  onToggle,
  disabled = false,
  helperText,
}) => {
  const [meetingTypes, setMeetingTypes] = useState([]);

  useEffect(() => {
    const fetchMeetingTypes = async () => {
      try {
        const data = await meetingsApi.getMeetingTypes();
        setMeetingTypes(data || []);
      } catch (err) {
        console.error('Failed to fetch meeting types:', err);
      }
    };
    fetchMeetingTypes();
  }, []);

  const handleToggle = (typeId) => {
    if (disabled) return;
    onToggle(typeId);
  };

  return (
    <Box>
      {helperText && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helperText}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {meetingTypes.map((type) => {
          const isSelected = selectedTypeIds.includes(type.id);
          return (
            <Chip
              key={type.id}
              label={
                isSelected ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: type.color || '#6b7280',
                        flexShrink: 0,
                      }}
                    />
                    {type.name}
                  </Box>
                ) : type.name
              }
              onClick={disabled ? undefined : () => handleToggle(type.id)}
              sx={{
                backgroundColor: isSelected ? '#e0e7ff' : '#E5E7EB',
                color: '#374151',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                border: isSelected ? '1px solid #3b82f6' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: disabled ? (isSelected ? '#e0e7ff' : '#E5E7EB') : (isSelected ? '#c7d2fe' : '#D1D5DB'),
                },
              }}
            />
          );
        })}
        {meetingTypes.length === 0 && (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No meeting types configured
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MeetingTypesSelector;
